import { FacebookHelper } from "components/helpers/index";
import { Script } from "gatsby";
import { useEffect } from "react";

export const Head = ({ data, pageContext, location }) => {
  const contentPage = data.contentfulContentPage;
  const siteConfig = data.contentfulSiteConfiguration;
  const {
    enableGoogleOptimizeAntiFlickerScript,
    node_locale,
    title,
    description,
    keywords,
    metaTitle,
    excludePageFromExternalSearch,
    socialMediaSharedImage,
    blocks,
    pageTheme,
  } = contentPage;
  const { footerLogo, googleOptimizeContainerId } = siteConfig;

  const isStartPage = pageContext.slug === "/";
  const logo = footerLogo?.file?.url;
  const ogImage =
    FacebookHelper.extendContentfulUrlWithHttps(socialMediaSharedImage?.file?.url) ||
    FacebookHelper.getFacebookImageByCurrentBlocks(blocks ?? [], siteConfig);
  const facebookLocale = FacebookHelper.getFacebookLocale();
  const facebookUrl = FacebookHelper.getFaceBookUrl(location.pathname);
  const metaRobotsContent = excludePageFromExternalSearch ? "noindex,follow" : "index,follow";

  useEffect(() => {
    /* global CookieScript */
    if (typeof CookieScript === "undefined") return;

    const langCode = node_locale?.split(/[\W_]/)?.[0]?.toLowerCase();

    CookieScript?.instance?.applyTranslationByCode(langCode);
  }, [node_locale]);

  useEffect(() => {
    const sessionStorageKey = "cookieScriptRenewed";

    if (typeof CookieScript === "undefined" || sessionStorage.getItem(sessionStorageKey)) return;

    const currentCookieScriptState = CookieScript.instance.currentState();
    const action = currentCookieScriptState?.action;
    const acceptedCategories = currentCookieScriptState?.categories;

    if (action === "reject") {
      //This means the user rejected all cookies except the "strict" one since these _cannot_ be rejected.
      //So its ok for us to renew the "strict" cookies
      sessionStorage.setItem(sessionStorageKey, true);
      CookieScript.instance.acceptAction(["strict"]);
    } else if (action === "accept") {
      sessionStorage.setItem(sessionStorageKey, true);
      CookieScript.instance.acceptAction(acceptedCategories);
    } else {
      // We could end up here if action is "undefined" which means the user has not interacted with the cookie banner yet.
      // then we do not need to renew the cookies in the future since they will get the latest one when they interact with the banner.
      sessionStorage.setItem(sessionStorageKey, true);
    }
  });

  return (
    <>
      <html lang={node_locale} className={pageTheme ? `page-theme-${pageTheme.toLowerCase()}` : "page-theme-none"} />
      <title>{title}</title>
      <meta property="og:url" content={facebookUrl} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="theme-color" content="#000000" />
      <meta name="robots" content={metaRobotsContent} />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content={facebookLocale} />
      <meta name="google-site-verification" content="cBp8YSEhfxUz0JyC9Z_JMFKqFfpmI9xODTX9P2XR6-Y" />
      <meta name="cache-control" httpEquiv="Cache-Control" content="public, max-age=0, must-revalidate" />
      <link rel="manifest" href="/manifest.webmanifest" />
      <link rel="icon" type="image/x-icon" href="/favicon.ico" />
      <link rel="icon" sizes="192x192" href="/icons/icon-192x192.png" />
      {isStartPage && (
        <Script type="application/ld+json">
          {`
            {
              "@context" : "https://schema.org",
              "@type" : "Organization",
              "name" : "Skara Sommarland",
              "url" : "https://www.sommarland.se",
              "logo": "${logo}",
              "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "Skara Sommarland AB",
                  "addressLocality": "Axvall",
                  "postalCode": "532 92",
                  "addressCountry": "Sweden"
              },
              "contactPoint": [
                  {
                      "@type" : "ContactPoint",
                      "telephone" : "+46-010-7087000",
                      "contactType" : "customer service",
                      "areaServed" : "SE",
                      "email": "info@sommarland.se"
                  }
              ],
              "sameAs" : [
                  "https://www.facebook.com/Kolmarden/",
                  "https://www.youtube.com/channel/UCxPMaMEpjGWXAh1Ro0aMIVw",
                  "https://www.instagram.com/kolmardensdjurpark/"
              ]
            }
          `}
        </Script>
      )}
      {googleOptimizeContainerId && enableGoogleOptimizeAntiFlickerScript && (
        <>
          <style>{`.async-hide { opacity: 0 !important}`}</style>
          <Script>
            {`(function(a,s,y,n,c,h,i,d,e){s.className+=' '+y;h.start=1*new Date;
              h.end=i=function(){s.className=s.className.replace(RegExp(' ?'+y),'')};
              (a[n]=a[n]||[]).hide=h;setTimeout(function(){i();h.end=null},c);h.timeout=c;
              })(window,document.documentElement,'async-hide','dataLayer',4000,
              {'${googleOptimizeContainerId}':true});`}
          </Script>
        </>
      )}
    </>
  );
};
